import type { PageContext, Qualification } from "./contract";

// The forum's admission pattern: known automation is excluded; observed
// disguised-browser families wait for trusted input or focused, visible dwell.
// This changes analytics admission, never access to the website or AI tools.
// Keep generic tokens delimited: bare "bot" also matches real Cubot phones.
const BOT_UA = /googlebot|bingbot|gptbot|oai-searchbot|chatgpt-user|claudebot|perplexitybot|applebot|amazonbot|bytespider|facebookexternal|meta-externalagent|ahrefsbot|semrushbot|uptimerobot|bot\/|\(bot;|crawler|spider|headlesschrome|lighthouse|screaming frog|sitebulb|curl|wget|python-requests|node-fetch|axios|scrapy|pingdom|prerender|google-inspectiontool/i;
const PLAIN_CHROME = /^Mozilla\/5\.0 \((Windows NT 10\.0; Win64; x64|Macintosh; Intel Mac OS X 10_15_7)\) AppleWebKit\/537\.36 \(KHTML, like Gecko\) Chrome\/(\d+)\.0\.0\.0 Safari\/537\.36$/;
export const FOCUSED_DWELL_MS = 10_000;
export const BOT_RULE_VERSION = "2026-10-26.1";

export function botReason(userAgent: string, context: Pick<PageContext, "webdriver">): string | null {
  if (context.webdriver) return "webdriver";
  if (!userAgent.trim()) return "missing-user-agent";
  return BOT_UA.test(userAgent) ? "automation-user-agent" : null;
}

export function syntheticCohort(userAgent: string, context: PageContext, hostname: string): string | null {
  // Attribution from another site keeps the cohort narrow. A same-site
  // navigation also leaves this family, as on the forum.
  if (context.referrer || !hostname || !PLAIN_CHROME.test(userAgent)) return null;
  const major = Number(PLAIN_CHROME.exec(userAgent)?.[2]);
  // 112 one-page visitors across 25 countries on Oct 26; their viewport and
  // timezone rotate while these four signals remain fixed.
  if (major === 151 && context.language === "en-US" && context.screenWidth === 1920 && context.screenHeight === 1080) {
    return "chrome151-fixed-screen";
  }
  // Separate 56-visitor family: rotated desktop sizes and Chrome 148–150,
  // Chinese language and Shanghai/Singapore browser timezone. No country rule.
  if (major >= 148 && major <= 150 && context.language === "zh-CN"
    && ["Asia/Shanghai", "Asia/Singapore"].includes(context.timezone)
    && context.screenWidth >= 1200 && context.screenWidth <= 1400
    && context.screenHeight >= 1000 && context.screenHeight <= 1400) {
    return "chrome148-150-variable-screen";
  }
  return null;
}

export function isAdmitted(userAgent: string, context: PageContext, hostname: string): boolean {
  if (botReason(userAgent, context)) return false;
  return !syntheticCohort(userAgent, context, hostname)
    || context.qualification === "trusted-activity"
    || (context.qualification === "focused-dwell" && (context.focusedMs ?? 0) >= FOCUSED_DWELL_MS);
}

// Ported from ht-forum's synthetic-visitors.ts. Scroll/mousemove cannot
// qualify: scripts and fragment navigation can generate trusted scroll events.
export function createQualification(startedAt: number, attending: boolean) {
  let checkpoint = startedAt;
  let focusedMs = 0;
  let active = attending;
  let qualified: Qualification | undefined;
  const sample = (now: number) => {
    if (active) focusedMs += Math.max(0, now - checkpoint);
    checkpoint = now;
    if (!qualified && focusedMs >= FOCUSED_DWELL_MS) qualified = "focused-dwell";
  };
  return {
    attendance(now: number, next: boolean) { sample(now); active = next; },
    activity(now: number, type: string, trusted: boolean) {
      sample(now);
      if (!qualified && active && trusted && ["pointerdown", "keydown", "touchstart", "wheel"].includes(type)) qualified = "trusted-activity";
    },
    read(now: number) { sample(now); return qualified; },
    focusedTime() { return Math.min(focusedMs, 86_400_000); },
    remaining() { return Math.max(0, FOCUSED_DWELL_MS - focusedMs); },
  };
}
